import { QUOTATION_API_URL } from "../../../../config";
import { LogCategory } from "../../../../config/logging";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { buildQuotationApiHeaders } from "./quotationApiHeaders";

export interface UploadQuotationNotaFiscalInput {
	orcId: number;
	userId: number;
	files: File[];
	observacao?: string;
}

export interface UploadQuotationComprovanteInput {
	orcId: number;
	userId: number;
	files: File[];
}

interface UploadResult {
	uploadedCount: number;
	raw: any;
}

function getQuotationApiBaseUrl(): string {
	const base = String(QUOTATION_API_URL || "").trim();
	if (!base || !base.startsWith("http")) {
		throw new Error("Base da API de orcamento invalida para upload de documentos.");
	}
	return base.endsWith("/") ? base.slice(0, -1) : base;
}

async function parseJsonSafe(response: Response): Promise<any> {
	try {
		return await response.json();
	} catch {
		return {};
	}
}

function parseErrorMessage(data: any, fallback: string): string {
	return (
		data?.message ||
		data?.error ||
		data?.msg ||
		data?.data?.message ||
		data?.data?.error ||
		fallback
	);
}

function buildMultipartHeaders(): Record<string, string> {
	const headers = buildQuotationApiHeaders();
	const { ["Content-Type"]: _ignored, ...rest } = headers;
	return rest;
}

export async function uploadQuotationNotaFiscal(
	input: UploadQuotationNotaFiscalInput,
): Promise<UploadResult> {
	if (!input.files.length) {
		return { uploadedCount: 0, raw: null };
	}

	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/orcamento-nota-fiscal`;
	const headers = buildMultipartHeaders();
	const form = new FormData();

	form.append("orc_id", String(input.orcId));
	form.append("user_usucadastro", String(input.userId));
	form.append("user_usuultalteracao", String(input.userId));
	if (input.observacao?.trim()) {
		form.append("onf_observacao", input.observacao.trim());
	}
	for (const file of input.files) {
		form.append("nota_fiscal", file);
	}

	sendToTerminal(
		"[QUOTATION_DOCS] Upload de nota fiscal iniciado",
		{
			endpoint,
			orcId: input.orcId,
			files: input.files.map((file) => file.name),
		},
		LogCategory.BUSINESS,
	);

	const response = await fetch(endpoint, {
		method: "POST",
		headers,
		body: form,
		mode: "cors",
	});
	const data = await parseJsonSafe(response);

	if (!response.ok) {
		throw new Error(parseErrorMessage(data, "Erro ao enviar nota fiscal."));
	}

	return {
		uploadedCount: input.files.length,
		raw: data,
	};
}

export async function uploadQuotationComprovante(
	input: UploadQuotationComprovanteInput,
): Promise<UploadResult> {
	if (!input.files.length) {
		return { uploadedCount: 0, raw: null };
	}

	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/createOrcamentoComprovante`;
	const headers = buildMultipartHeaders();
	const form = new FormData();

	form.append("orc_id", String(input.orcId));
	form.append("user_usucadastro", String(input.userId));
	form.append("user_usuultalteracao", String(input.userId));
	for (const file of input.files) {
		form.append("comprovante", file);
	}

	sendToTerminal(
		"[QUOTATION_DOCS] Upload de comprovante iniciado",
		{
			endpoint,
			orcId: input.orcId,
			files: input.files.map((file) => file.name),
		},
		LogCategory.BUSINESS,
	);

	const response = await fetch(endpoint, {
		method: "POST",
		headers,
		body: form,
		mode: "cors",
	});
	const data = await parseJsonSafe(response);

	if (!response.ok) {
		throw new Error(parseErrorMessage(data, "Erro ao enviar comprovante."));
	}

	return {
		uploadedCount: input.files.length,
		raw: data,
	};
}
